import React, { useEffect, useState, useReducer, useCallback } from "react";
import {
    Button, IconButton, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, makeStyles, Chip
} from "@material-ui/core";
import { DeleteOutline, Edit, Event } from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import HolidayModal from "../../components/HolidayModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
}));

const reducer = (state, action) => {
    switch (action.type) {
        case "LOAD_HOLIDAYS":
            return action.payload;
        case "UPDATE_HOLIDAY":
            const holiday = action.payload;
            const index = state.findIndex(h => h.id === holiday.id);
            if (index !== -1) {
                state[index] = holiday;
                return [...state];
            }
            return [holiday, ...state];
        case "DELETE_HOLIDAY":
            return state.filter(h => h.id !== action.payload);
        case "RESET":
            return [];
        default:
            return state;
    }
};

const Holidays = () => {
    const classes = useStyles();
    const [holidays, dispatch] = useReducer(reducer, []);
    const [loading, setLoading] = useState(false);
    const [holidayModalOpen, setHolidayModalOpen] = useState(false);
    const [selectedHoliday, setSelectedHoliday] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deletingHoliday, setDeletingHoliday] = useState(null);

    const fetchHolidays = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/holidays");
            dispatch({ type: "LOAD_HOLIDAYS", payload: data });
            setLoading(false);
        } catch (err) {
            toastError(err);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchHolidays();
    }, [fetchHolidays]);

    const handleOpenHolidayModal = () => {
        setHolidayModalOpen(true);
        setSelectedHoliday(null);
    };

    const handleCloseHolidayModal = () => {
        setHolidayModalOpen(false);
        setSelectedHoliday(null);
    };

    const handleEditHoliday = (holiday) => {
        setSelectedHoliday(holiday);
        setHolidayModalOpen(true);
    };

    const handleDeleteHoliday = async (holidayId) => {
        try {
            await api.delete(`/holidays/${holidayId}`);
            toast.success("Feriado excluído com sucesso!");
            dispatch({ type: "DELETE_HOLIDAY", payload: holidayId });
        } catch (err) {
            toastError(err);
        }
        setDeletingHoliday(null);
        setConfirmModalOpen(false);
    };

    const formatDate = (dateString) => {
        try {
            return format(parseISO(dateString), "dd/MM/yyyy", { locale: ptBR });
        } catch {
            return dateString;
        }
    };

    return (
        <MainContainer>
            <HolidayModal
                open={holidayModalOpen}
                onClose={handleCloseHolidayModal}
                holidayId={selectedHoliday && selectedHoliday.id}
                reload={fetchHolidays}
            />
            <ConfirmationModal
                title={deletingHoliday && `Excluir feriado "${deletingHoliday.name}"?`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteHoliday(deletingHoliday.id)}
            >
                Esta ação não pode ser desfeita.
            </ConfirmationModal>
            <MainHeader>
                <Title><Event style={{ marginRight: 8 }} /> Feriados</Title>
                <MainHeaderButtonsWrapper>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenHolidayModal}
                    >
                        Adicionar Feriado
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">Nome</TableCell>
                                <TableCell align="center">Data</TableCell>
                                <TableCell align="center">Tipo</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {holidays.map((holiday) => (
                                <TableRow key={holiday.id}>
                                    <TableCell align="center">{holiday.name}</TableCell>
                                    <TableCell align="center">{formatDate(holiday.date)}</TableCell>
                                    <TableCell align="center">
                                        {holiday.recurring ? (
                                            <Chip label="Recorrente" color="primary" size="small" />
                                        ) : (
                                            <Chip label="Único" size="small" />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditHoliday(holiday)}
                                        >
                                            <Edit />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setDeletingHoliday(holiday);
                                                setConfirmModalOpen(true);
                                            }}
                                        >
                                            <DeleteOutline />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </MainContainer>
    );
};

export default Holidays;
